import { useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Pencil, Plus, Search, Trash2, User } from 'lucide-react'
import type { ColumnDef } from '@tanstack/react-table'
import { apiClient } from '@/lib/api.ts'
import {
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorState,
  IconButton,
  Input,
  LoadingState,
  Modal,
  PageHeader,
  Textarea,
  useToast,
} from '@/components/ui'

// ─── Types ────────────────────────────────────────────────────────────────────

interface SupplierMapping {
  id: string
  manufacturerName: string
  supplierName: string
  priority: number
  certificationNote: string | null
  contactName: string | null
  tanssContactId: string | null
  tanssCompanyId: string | null
  tanssCompanyName: string | null
  createdAt: string
  updatedAt: string
}

interface TanssCompany {
  id: number | string
  name: string
  email: string | null
}

interface TanssContact {
  id: number | string
  name: string
  email: string | null
}

interface MappingFormData {
  manufacturerName: string
  supplierName: string
  priority: number
  certificationNote: string
  contactName: string
  tanssContactId: string
  tanssCompanyId: string
  tanssCompanyName: string
}

const EMPTY_FORM: MappingFormData = {
  manufacturerName: '',
  supplierName: '',
  priority: 3,
  certificationNote: '',
  contactName: '',
  tanssContactId: '',
  tanssCompanyId: '',
  tanssCompanyName: '',
}

// ─── TANSS contact picker sub-component ──────────────────────────────────────

interface TanssPickerProps {
  contactName: string
  onSelect: (contact: { name: string; id: string; companyId: string; companyName: string }) => void
  onClear: () => void
}

function TanssContactPicker({ contactName, onSelect, onClear }: TanssPickerProps) {
  const [companyQuery, setCompanyQuery] = useState('')
  const [selectedCompany, setSelectedCompany] = useState<TanssCompany | null>(null)
  const [showPicker, setShowPicker] = useState(false)

  const companiesQuery = useQuery<{ data: TanssCompany[] }>({
    queryKey: ['tanss-companies-search', companyQuery],
    queryFn: () =>
      companyQuery.length >= 2
        ? apiClient.get('/tanss/companies/search', { params: { q: companyQuery } }).then((r) => r.data)
        : Promise.resolve({ data: [] }),
    enabled: companyQuery.length >= 2,
  })

  const contactsQuery = useQuery<{ data: TanssContact[] }>({
    queryKey: ['tanss-contacts', selectedCompany?.id],
    queryFn: () =>
      selectedCompany
        ? apiClient.get(`/tanss/companies/${selectedCompany.id}/persons`).then((r) => r.data)
        : Promise.resolve({ data: [] }),
    enabled: !!selectedCompany,
  })

  if (!showPicker) {
    return (
      <div className="flex items-center gap-2">
        {contactName ? (
          <div className="flex items-center gap-2 flex-1">
            <User size={14} className="text-neutral-400 shrink-0" />
            <span className="text-sm text-neutral-700">{contactName}</span>
            <button
              type="button"
              onClick={() => { onClear(); setSelectedCompany(null); setCompanyQuery('') }}
              className="text-xs text-neutral-400 hover:text-danger-600 ml-auto"
            >
              Entfernen
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowPicker(true)}
            className="flex items-center gap-1 text-sm text-brand-600 hover:text-brand-700"
          >
            <Search size={13} />
            TANSS-Kontakt wählen
          </button>
        )}
      </div>
    )
  }

  const companies = companiesQuery.data?.data ?? []
  const contacts = contactsQuery.data?.data ?? []

  return (
    <div className="space-y-2 border border-neutral-200 rounded-lg p-3 bg-neutral-50">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-neutral-600">TANSS-Kontakt auswählen</span>
        <button
          type="button"
          onClick={() => setShowPicker(false)}
          className="text-xs text-neutral-400 hover:text-neutral-600"
        >
          Schließen
        </button>
      </div>

      <div>
        <label className="text-xs text-neutral-500 mb-1 block">Firma suchen (mind. 2 Zeichen)</label>
        <Input
          value={companyQuery}
          onChange={(e) => { setCompanyQuery(e.target.value); setSelectedCompany(null) }}
          placeholder="Firmenname eingeben…"
          className="text-sm"
        />
        {companies.length > 0 && (
          <div className="border border-neutral-200 rounded mt-1 bg-white shadow-sm max-h-40 overflow-y-auto">
            {companies.map((c) => (
              <button
                key={String(c.id)}
                type="button"
                onClick={() => { setSelectedCompany(c); setCompanyQuery(c.name) }}
                className={`w-full text-left px-3 py-1.5 text-sm hover:bg-brand-50 ${
                  selectedCompany?.id === c.id ? 'bg-brand-100 font-medium' : ''
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {selectedCompany && (
        <div>
          <label className="text-xs text-neutral-500 mb-1 block">Ansprechpartner</label>
          {contactsQuery.isLoading ? (
            <p className="text-xs text-neutral-400">Lade Kontakte…</p>
          ) : contacts.length === 0 ? (
            <p className="text-xs text-neutral-400">Keine Kontakte gefunden</p>
          ) : (
            <div className="border border-neutral-200 rounded bg-white shadow-sm max-h-40 overflow-y-auto">
              {contacts.map((p) => (
                <button
                  key={String(p.id)}
                  type="button"
                  onClick={() => {
                    onSelect({
                      name: p.name,
                      id: String(p.id),
                      companyId: String(selectedCompany.id),
                      companyName: selectedCompany.name,
                    })
                    setShowPicker(false)
                  }}
                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-brand-50"
                >
                  <span>{p.name}</span>
                  {p.email && <span className="ml-2 text-xs text-neutral-400">{p.email}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Priority display ─────────────────────────────────────────────────────────

function PriorityDots({ priority }: { priority: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <span
          key={n}
          className={`inline-block w-2 h-2 rounded-full ${
            n <= priority ? 'bg-brand-500' : 'bg-neutral-200'
          }`}
        />
      ))}
      <span className="ml-1 text-xs text-neutral-500">{priority}</span>
    </div>
  )
}

// ─── Create / Edit modal ──────────────────────────────────────────────────────

interface MappingModalProps {
  open: boolean
  initial: MappingFormData
  isEdit: boolean
  isSaving: boolean
  onSave: (data: MappingFormData) => void
  onClose: () => void
}

function MappingModal({ open, initial, isEdit, isSaving, onSave, onClose }: MappingModalProps) {
  const [form, setForm] = useState<MappingFormData>(initial)

  const set = useCallback(
    (key: keyof MappingFormData, value: string | number) =>
      setForm((prev) => ({ ...prev, [key]: value })),
    [],
  )

  const handleOpen = useCallback(() => setForm(initial), [initial])

  const handleTanssSelect = useCallback(
    (c: { name: string; id: string; companyId: string; companyName: string }) => {
      setForm((prev) => ({
        ...prev,
        contactName: c.name,
        tanssContactId: c.id,
        tanssCompanyId: c.companyId,
        tanssCompanyName: c.companyName,
      }))
    },
    [],
  )

  const handleTanssClear = useCallback(() => {
    setForm((prev) => ({
      ...prev,
      contactName: '',
      tanssContactId: '',
      tanssCompanyId: '',
      tanssCompanyName: '',
    }))
  }, [])

  const canSave = form.manufacturerName.trim().length > 0 && form.supplierName.trim().length > 0

  return (
    <Modal
      open={open}
      onClose={onClose}
      onOpenChange={(o) => { if (o) handleOpen(); else onClose() }}
      title={isEdit ? 'Mapping bearbeiten' : 'Neues Mapping anlegen'}
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={isSaving}>Abbrechen</Button>
          <Button
            variant="primary"
            onClick={() => onSave(form)}
            disabled={!canSave || isSaving}
            loading={isSaving}
          >
            {isEdit ? 'Speichern' : 'Anlegen'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Hersteller <span className="text-danger-500">*</span>
            </label>
            <Input
              value={form.manufacturerName}
              onChange={(e) => set('manufacturerName', e.target.value)}
              placeholder="z.B. Apple, Dell, HP…"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Lieferant <span className="text-danger-500">*</span>
            </label>
            <Input
              value={form.supplierName}
              onChange={(e) => set('supplierName', e.target.value)}
              placeholder="z.B. Also, Ingram Micro…"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Priorität (1 = höchste)
          </label>
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => set('priority', n)}
                className={`w-9 h-9 rounded-lg border text-sm font-semibold transition-colors ${
                  form.priority === n
                    ? 'border-brand-500 bg-brand-500 text-white'
                    : 'border-neutral-200 bg-white text-neutral-600 hover:border-brand-300'
                }`}
              >
                {n}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">
            Zertifizierungsnotiz
          </label>
          <Textarea
            value={form.certificationNote}
            onChange={(e) => set('certificationNote', e.target.value)}
            placeholder="z.B. Apple Authorised Reseller, Microsoft Gold Partner…"
            rows={2}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Ansprechpartner (optional)
          </label>
          <TanssContactPicker
            contactName={form.contactName}
            onSelect={handleTanssSelect}
            onClear={handleTanssClear}
          />
          {form.contactName && (
            <div className="mt-1">
              <label className="text-xs text-neutral-500 block mb-0.5">Name überschreiben</label>
              <Input
                value={form.contactName}
                onChange={(e) => set('contactName', e.target.value)}
                className="text-sm"
              />
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function HerstellerMappingPage() {
  const { toast } = useToast()
  const qc = useQueryClient()

  const [modalOpen, setModalOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<SupplierMapping | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<SupplierMapping | null>(null)

  const { data, isLoading, error, refetch } = useQuery<{ data: SupplierMapping[] }>({
    queryKey: ['hersteller-mapping'],
    queryFn: () => apiClient.get('/einkauf/hersteller-mapping').then((r) => r.data),
  })

  const createMut = useMutation({
    mutationFn: (body: MappingFormData) =>
      apiClient.post('/einkauf/hersteller-mapping', {
        manufacturerName: body.manufacturerName.trim(),
        supplierName: body.supplierName.trim(),
        priority: body.priority,
        certificationNote: body.certificationNote || null,
        contactName: body.contactName || null,
        tanssContactId: body.tanssContactId || null,
        tanssCompanyId: body.tanssCompanyId || null,
        tanssCompanyName: body.tanssCompanyName || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hersteller-mapping'] })
      setModalOpen(false)
      toast({ title: 'Mapping angelegt', tone: 'success' })
    },
    onError: (e: unknown) =>
      toast({ title: 'Fehler beim Anlegen', description: errMsg(e), tone: 'danger' }),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: MappingFormData }) =>
      apiClient.patch(`/einkauf/hersteller-mapping/${id}`, {
        manufacturerName: body.manufacturerName.trim(),
        supplierName: body.supplierName.trim(),
        priority: body.priority,
        certificationNote: body.certificationNote || null,
        contactName: body.contactName || null,
        tanssContactId: body.tanssContactId || null,
        tanssCompanyId: body.tanssCompanyId || null,
        tanssCompanyName: body.tanssCompanyName || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hersteller-mapping'] })
      setEditTarget(null)
      toast({ title: 'Mapping gespeichert', tone: 'success' })
    },
    onError: (e: unknown) =>
      toast({ title: 'Fehler beim Speichern', description: errMsg(e), tone: 'danger' }),
  })

  const deleteMut = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/einkauf/hersteller-mapping/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hersteller-mapping'] })
      setDeleteTarget(null)
      toast({ title: 'Mapping gelöscht', tone: 'success' })
    },
    onError: (e: unknown) =>
      toast({ title: 'Fehler beim Löschen', description: errMsg(e), tone: 'danger' }),
  })

  const rows = data?.data ?? []

  const columns: ColumnDef<SupplierMapping, unknown>[] = [
    {
      id: 'manufacturerName',
      accessorKey: 'manufacturerName',
      header: 'Hersteller',
      cell: ({ row }) => (
        <span className="font-medium text-neutral-900">{row.original.manufacturerName}</span>
      ),
    },
    {
      id: 'supplierName',
      accessorKey: 'supplierName',
      header: 'Lieferant',
      cell: ({ row }) => <span className="text-neutral-700">{row.original.supplierName}</span>,
    },
    {
      id: 'priority',
      accessorKey: 'priority',
      header: 'Priorität',
      cell: ({ row }) => <PriorityDots priority={row.original.priority} />,
    },
    {
      id: 'certificationNote',
      accessorKey: 'certificationNote',
      header: 'Zertifizierungsnotiz',
      cell: ({ row }) =>
        row.original.certificationNote ? (
          <span className="text-sm text-neutral-600 line-clamp-1">{row.original.certificationNote}</span>
        ) : (
          <span className="text-xs text-neutral-300">—</span>
        ),
    },
    {
      id: 'contactName',
      accessorKey: 'contactName',
      header: 'Ansprechpartner',
      cell: ({ row }) =>
        row.original.contactName ? (
          <span className="flex items-center gap-1 text-sm text-neutral-600">
            <User size={12} className="text-neutral-400" />
            {row.original.contactName}
            {row.original.tanssCompanyName && (
              <span className="text-xs text-neutral-400">· {row.original.tanssCompanyName}</span>
            )}
          </span>
        ) : (
          <span className="text-xs text-neutral-300">—</span>
        ),
    },
    {
      id: 'actions',
      header: '',
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex items-center justify-end gap-1">
          <IconButton
            icon={<Pencil size={14} />}
            aria-label="Bearbeiten"
            size="xs"
            onClick={() => setEditTarget(row.original)}
          />
          <IconButton
            icon={<Trash2 size={14} />}
            aria-label="Löschen"
            size="xs"
            variant="ghost"
            className="text-neutral-400 hover:text-danger-600"
            onClick={() => setDeleteTarget(row.original)}
          />
        </div>
      ),
    },
  ]

  const editForm: MappingFormData = editTarget
    ? {
        manufacturerName: editTarget.manufacturerName,
        supplierName: editTarget.supplierName,
        priority: editTarget.priority,
        certificationNote: editTarget.certificationNote ?? '',
        contactName: editTarget.contactName ?? '',
        tanssContactId: editTarget.tanssContactId ?? '',
        tanssCompanyId: editTarget.tanssCompanyId ?? '',
        tanssCompanyName: editTarget.tanssCompanyName ?? '',
      }
    : EMPTY_FORM

  return (
    <div className="space-y-4">
      <PageHeader
        title="Hersteller-Lieferanten-Mapping"
        description="Bevorzugte Lieferanten je Hersteller mit Einkaufspriorität und Zertifizierungsnotizen"
        actions={
          <Button variant="primary" leadingIcon={<Plus size={16} />} onClick={() => setModalOpen(true)}>
            Mapping anlegen
          </Button>
        }
      />

      <Card>
        {isLoading ? (
          <LoadingState label="Lade Mappings…" />
        ) : error ? (
          <ErrorState
            description="Mappings konnten nicht geladen werden."
            onRetry={refetch}
          />
        ) : (
          <DataTable
            data={rows}
            columns={columns}
            getRowId={(row) => row.id}
            pagination={{ pageSize: 25 }}
            searchable
            searchPlaceholder="Hersteller oder Lieferant suchen…"
            emptyState={
              <EmptyState
                icon={<Plus size={32} />}
                title="Noch keine Mappings"
                description="Legen Sie das erste Hersteller-Lieferanten-Mapping an."
                action={
                  <Button variant="primary" onClick={() => setModalOpen(true)}>
                    Jetzt anlegen
                  </Button>
                }
              />
            }
          />
        )}
      </Card>

      {/* Create modal — key ensures form resets on each open */}
      <MappingModal
        key={modalOpen ? 'create-open' : 'create-closed'}
        open={modalOpen}
        initial={EMPTY_FORM}
        isEdit={false}
        isSaving={createMut.isPending}
        onSave={(form) => createMut.mutate(form)}
        onClose={() => setModalOpen(false)}
      />

      {/* Edit modal — key on row id ensures form resets when switching rows */}
      {editTarget && (
        <MappingModal
          key={editTarget.id}
          open={true}
          initial={editForm}
          isEdit={true}
          isSaving={updateMut.isPending}
          onSave={(form) => updateMut.mutate({ id: editTarget.id, body: form })}
          onClose={() => setEditTarget(null)}
        />
      )}

      {/* Delete confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Mapping löschen"
        message={
          deleteTarget
            ? `Soll das Mapping „${deleteTarget.manufacturerName} → ${deleteTarget.supplierName}" wirklich gelöscht werden?`
            : ''
        }
        confirmLabel="Löschen"
        variant="danger"
        loading={deleteMut.isPending}
        onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  )
}

// ─── Error helper ─────────────────────────────────────────────────────────────

function errMsg(e: unknown): string {
  if (e && typeof e === 'object' && 'response' in e) {
    const r = (e as { response?: { data?: { error?: string } } }).response
    if (r?.data?.error) return r.data.error
  }
  return (e as Error)?.message ?? 'Unbekannter Fehler'
}
